import { gql } from '@apollo/client';
import { TransitionPanel } from '@components/transition';
import { GetWorkspaceContextDataQuery, useGetWorkspaceContextDataQuery } from '@generated/graphql';
import { navigate } from 'gatsby';
import { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { NonexistentWorkspacePage } from './NonexistentWorkspacePage';

gql`
  query GetWorkspaceContextData($workspaceId: String) {
    getWorkspaces(workspaceId: $workspaceId) {
      description
      name
      organizationId
      status
      tags
      workspaceId
    }
  }
`;

type WorkspaceContextType = {
  workspace: GetWorkspaceContextDataQuery['getWorkspaces'][number];
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  refetch: () => Promise<unknown>;
};

const WorkspaceContext = createContext<WorkspaceContextType | null>({
  workspace: null,
  startPolling: () => {},
  stopPolling: () => {},
  refetch: async () => {},
});

export const WorkspaceContextProvider = ({
  workspaceId,
  children,
}: PropsWithChildren<{ workspaceId: string }>) => {
  const workspaceContextQuery = useGetWorkspaceContextDataQuery({
    variables: { workspaceId },
  });

  const value = useMemo(() => {
    return {
      ...workspaceContextQuery,
      startPolling: workspaceContextQuery.startPolling,
      stopPolling: workspaceContextQuery.stopPolling,
      refetch: workspaceContextQuery.refetch,
      workspace: workspaceContextQuery?.data?.getWorkspaces?.[0],
    };
  }, [
    workspaceContextQuery.startPolling,
    workspaceContextQuery.stopPolling,
    workspaceContextQuery.refetch,
    workspaceContextQuery.data,
  ]);

  if (workspaceId && !workspaceContextQuery.loading && workspaceContextQuery.error) {
    void navigate('/', { state: { error: workspaceContextQuery.error?.message } });
  }

  if (workspaceContextQuery.loading || workspaceContextQuery.error) {
    return <TransitionPanel />;
  }

  if (
    !workspaceContextQuery.loading &&
    !workspaceContextQuery.error &&
    !workspaceContextQuery.data?.getWorkspaces?.[0]
  ) {
    return <NonexistentWorkspacePage />;
  }

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useCurrentWorkspace = () => useContext(WorkspaceContext);
export const useCurrentWorkspaceId = () => {
  const { workspace } = useCurrentWorkspace();
  return workspace?.workspaceId;
};
export const useCurrentOrganizationId = () => {
  const { workspace } = useCurrentWorkspace();
  return workspace?.organizationId;
};
